<template>
  <el-drawer
    v-model="visible"
    append-to-body
    class="xbb-form-drawer"
    direction="btt"
    :show-close="false"
    size="90%"
    :with-header="false"
  >
    <iframe
      id="xbbiframe"
      v-loading="iframeLoading"
      allow="geolocation"
      frameborder="0"
      :src="xbbUrl"
      style="height: calc(100% - env(safe-area-inset-bottom)); width: 100%"
    ></iframe>
  </el-drawer>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount } from 'vue'
import utils from '@/utils'
import xbb from '@xbb/xbb-utils'
import { useAppStore } from '@/store/useAppStore'
import AppMessage from '@/components/message/index.js'
import { registerGlobalCallback } from '@/utils/h5/global-callback'

defineOptions({
  name: 'AiUseXbbForm'
})

const XBB_MESSAGE_TYPE = {
  INIT: 'aiInit',
  CLOSE: 'aiClose',
  LOCATION: 'aiLocation',
  SUCCESS: 'aiSuccess',
  START_VOICE_INPUT: 'startXbbVoiceInput',
  STOP_VOICE_INPUT: 'stopXbbVoiceInput'
}

const props = defineProps({
  xbbUrl: {
    type: String,
    required: true
  },
  showXbb: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emits = defineEmits(['update:showXbb', 'update:loading', 'handleXbbSuccess'])

const visible = computed({
  get: () => props.showXbb,
  set: (val) => {
    emits('update:showXbb', val)
  }
})

const iframeLoading = computed({
  get: () => props.loading,
  set: (val) => {
    emits('update:loading', val)
  }
})

/**
 * 处理 XBB iframe 初始化消息
 * 向 iframe 发送初始化数据（用户信息、token 等）
 */
const handleXbbInit = () => {
  const iframe = document.getElementById('xbbiframe')
  iframe?.contentWindow?.postMessage(
    {
      type: 'aiInitData',
      data: {
        userId: utils.xbbLocalStorageGet('userId'),
        corpid: utils.xbbLocalStorageGet('corpid'),
        userName: utils.xbbLocalStorageGet('userName'),
        xbbAccessToken: utils.xbbLocalStorageGet('xbbAccessToken'),
        systemKind: utils.xbbLocalStorageGet('systemKind')
      }
    },
    '*'
  )
  iframeLoading.value = false
}

/**
 * 处理 XBB iframe 定位请求消息
 * 获取用户位置信息并发送给 iframe
 * @param {Object} e - 消息事件对象
 */
const handleXbbLocation = (e) => {
  const aiPostMessage = (type, position) => {
    const iframe = document.getElementById('xbbiframe')
    iframe?.contentWindow?.postMessage(
      {
        type: 'aiLocationData',
        data: {
          type,
          result:
            type === 1
              ? {
                  province: position.province,
                  city: position.city,
                  district: position.district,
                  adName: position.district,
                  address: position.address,
                  latitude: position.latitude,
                  longitude: position.longitude,
                  ...position
                }
              : position,
          uuid: e.data?.uuid
        }
      },
      '*'
    )
  }
  if (xbb.isDevEnv()) {
    aiPostMessage(1, {
      province: '浙江省',
      city: '杭州市',
      district: '滨江区',
      address: '浙江省杭州市滨江区滨盛路1505号银丰大厦',
      latitude: 30.21272,
      longitude: 120.212599
    })
  } else {
    utils.xbbGetLocation({
      onSuccess: (res) => {
        aiPostMessage(1, res)
      },
      onFail: () => {
        aiPostMessage(0, '定位失败，请重试或检查定位权限是否开启')
      }
    })
  }
}

/**
 * 处理 XBB iframe 关闭消息
 * 隐藏 iframe 并更新应用状态
 */
const handleXbbClose = () => {
  useAppStore().setIsXbbIframeShow(false)
  visible.value = false
}

const handleXbbSuccess = (e) => {
  console.log('走了抽组件流程')
  emits('handleXbbSuccess', e)
}

// 发送消息给uniapp
function sendMessageToUniapp(data) {
  if (!window.uni) return
  uni.postMessage({ data })
}

const handleXbbStartVoiceInput = (e) => {
  sendMessageToUniapp({ type: 'startVoiceInput' })
}

const handleXbbStopVoiceInput = (e) => {
  sendMessageToUniapp({ type: 'endVoiceInput' })
}

const xbbHandlers = {
  [XBB_MESSAGE_TYPE.INIT]: handleXbbInit,
  [XBB_MESSAGE_TYPE.LOCATION]: handleXbbLocation,
  [XBB_MESSAGE_TYPE.CLOSE]: handleXbbClose,
  [XBB_MESSAGE_TYPE.SUCCESS]: (e) => visible.value && handleXbbSuccess(e),
  [XBB_MESSAGE_TYPE.START_VOICE_INPUT]: (e) => visible.value && handleXbbStartVoiceInput(e),
  [XBB_MESSAGE_TYPE.STOP_VOICE_INPUT]: (e) => visible.value && handleXbbStopVoiceInput(e)
}

let unregisterUniappQuickInput

/**
 * 监听 XBB iframe 消息
 * 根据消息类型分发到对应的处理函数
 * @param {MessageEvent} e - 消息事件对象
 */
const xbbIframeInit = (e) => {
  const type = typeof e.data === 'string' ? e.data : e.data?.type
  const handler = xbbHandlers[type]
  if (handler) {
    handler(e)
  }
}

onMounted(() => {
  window.addEventListener('message', xbbIframeInit)
  // 监听从uniapp传递过来的消息
  unregisterUniappQuickInput = registerGlobalCallback('uniappQuickInput', (msg) => {
    if (!props.showXbb) return // 如果没有显示，就不处理后续的消息了
    const data = JSON.parse(msg)
    if (data.type === 'voiceFile') {
      const iframe = document.getElementById('xbbiframe')
      iframe?.contentWindow?.postMessage(
        {
          type: 'aiVoiceFile',
          data: {
            base64: data.data.base64,
            mimeType: data.data.mimeType
          }
        },
        '*'
      )
    } else if (data.type === 'voiceError') {
      AppMessage.error(data.data.msg)
    }
  })
})

onBeforeUnmount(() => {
  unregisterUniappQuickInput?.()
  unregisterUniappQuickInput = null
  window.removeEventListener('message', xbbIframeInit)
})

onBeforeUnmount(() => {
  useAppStore().setIsXbbIframeShow(false)
})
</script>

<style lang="scss">
.xbb-form-drawer {
  border-radius: 24px 24px 0px 0px;
  .el-drawer__body {
    border-radius: 24px 24px 0px 0px;
    overflow: hidden;
  }
}
</style>
